/**
 * Minimal, sanitized error-reporting helper.
 *
 * This is used by the frontend when a request or an interaction fails but we
 * still want a durable signal (e.g. for future integration with an error
 * pipeline). It intentionally NEVER prints:
 *   - Authorization headers or Bearer tokens
 *   - Supabase access / refresh tokens
 *   - Public quotation tokens
 *   - Passwords or environment variables
 *   - Full customer records
 *
 * In production it is a no-op. In development we emit a compact one-liner
 * via ``console.warn`` — never ``console.error`` — so the browser console
 * remains free of loud stack traces during normal operation.
 *
 * The helper accepts a *scope* string and an optional error-like value.
 * Any object passed in is aggressively stripped: only the HTTP status,
 * a short backend detail slug (if present), and the scope name are kept.
 */

const PROD = process.env.NODE_ENV === "production";

function _extractStatus(err) {
  const s = err?.response?.status;
  if (typeof s === "number") return s;
  return null;
}

function _extractDetail(err) {
  // Prefer the backend-provided ``detail`` slug when it is a plain string.
  // Anything else (objects, arrays, HTML pages) is discarded — those may
  // contain customer data or tokens depending on the endpoint.
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.length > 0 && d.length < 96) return d;
  return null;
}

export function reportError(scope, err) {
  if (PROD) return; // silent in production builds
  const status = _extractStatus(err);
  const detail = _extractDetail(err);
  const parts = [scope];
  if (status !== null) parts.push(`status=${status}`);
  if (detail) parts.push(`detail=${detail}`);
  // Never include headers, URLs, tokens or the raw error object.
  // eslint-disable-next-line no-console
  console.warn(`[quowada] ${parts.join(" · ")}`);
}

/**
 * Convert an unknown thrown value into a short, safe user-facing string.
 * Falls back to a generic message when the value could contain sensitive data.
 */
export function friendlyError(err, fallback) {
  const detail = _extractDetail(err);
  if (detail) return detail;
  return fallback;
}
